import React from 'react';
import { usePropertyDefs } from '../../../contexts/PropertyDefsContext';
import { useTaskService } from '../../../contexts/TaskServiceContext';
import {
	TYPE_META,
	readValue,
	writeValue,
	valueToDateInput,
} from '../../../services/propertyDefsModel';

/* The user-defined properties of one task, rendered as editable controls.
   Mounted inside TaskDetail, which is SHARED with the Work space — so this
   renders NOTHING when the surrounding schema is empty, which is exactly the
   case in Work (whose rules deliberately have no propertyDefs document).

   Writes go straight through the bound task service, so a Personal property
   lands in `personalTasks` and an Owners one in `ownersTasks` without this
   component knowing which space it is in.

   Every control writes on change rather than behind a Save button: these are
   single scalar fields on an already-persisted document, and a half-filled form
   you forgot to submit is a worse failure than an extra write. */
function PropertyFields({ task }) {
	const { activeDefs } = usePropertyDefs();
	const { updateTask } = useTaskService();

	if (!activeDefs.length) return null;

	const set = (def, raw) => {
		updateTask(task.id, writeValue(def, raw)).catch(() => {});
	};

	return (
		<div className='wt-pf'>
			{activeDefs.map((def) => {
				const value = readValue(task, def);
				const id = `pf-${task.id}-${def.slot}`;
				return (
					<div className='wt-pf__row' key={def.slot}>
						<label className='wt-pf__label' htmlFor={id}>
							<i
								className={`fa-sharp fa-solid ${TYPE_META[def.type].icon} wt-pf__icon`}
							/>
							{def.label}
						</label>

						{def.type === 'select' && (
							<select
								id={id}
								className='wt-pf__control'
								value={value ?? ''}
								onChange={(e) => set(def, e.target.value)}>
								<option value=''>—</option>
								{/* A value whose option was later deleted still shows, and is
								    listed here so selecting away from it is possible without
								    it silently vanishing from the dropdown first. */}
								{!def.options.includes(value) && value ? (
									<option value={value}>{value}</option>
								) : null}
								{def.options.map((o) => (
									<option key={o} value={o}>
										{o}
									</option>
								))}
							</select>
						)}

						{def.type === 'number' && (
							<input
								id={id}
								type='number'
								className='wt-pf__control'
								value={value ?? ''}
								onChange={(e) => set(def, e.target.value)}
							/>
						)}

						{def.type === 'date' && (
							<input
								id={id}
								type='date'
								className='wt-pf__control'
								value={valueToDateInput(value)}
								onChange={(e) => set(def, e.target.value)}
							/>
						)}

						{def.type === 'text' && (
							<input
								id={id}
								type='text'
								className='wt-pf__control'
								value={value ?? ''}
								onChange={(e) => set(def, e.target.value)}
							/>
						)}

						{def.type === 'checkbox' && (
							<input
								id={id}
								type='checkbox'
								className='wt-pf__check'
								checked={value === true}
								onChange={(e) => set(def, e.target.checked)}
							/>
						)}
					</div>
				);
			})}
		</div>
	);
}

export default PropertyFields;
